// Globals
import debounce from 'lodash.debounce';
import dayjs from 'dayjs';
import { ChangeEvent, memo, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { changeQuery, getAdminsList, selectQuery } from '../../feature';

// Models
import { Roles } from '@/models/admins.model';

// Components
import { Input, Select } from '@/ui';
import { StyledFilterRow } from '../../components';
import { RangePicker } from '@/components';
import type { RangePickerProps } from 'antd/es/date-picker';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { GetAdminsListDTO } from '../../dtos';
import { getRoleOptions } from '../../helpers/getOptions';
import { AdminsFiltersFields } from '../../helpers/types';
import { DateFormats } from '@/constants/dates';
import { formatDateForApiRequest } from '@/utils';

type IAdminsFiltersContainer = {
  children?: ReactNode;
  className?: string;
};

const AdminsFiltersContainer = ({ children, className }: IAdminsFiltersContainer) => {
  const { t } = useTranslation(['usersList', 'common']);
  const dispatch = useAppDispatch();
  const query = useAppState(selectQuery);
  const { canManage } = usePermissions();
  // The trailing actions column only exists when the admin can manage the admin panel.
  const canManageAdmins = canManage(Resource.ADMIN_PANEL);
  const dateRangeValue: RangePickerProps['value'] = [
    query?.[AdminsFiltersFields.DATE_FROM] ? (dayjs(query[AdminsFiltersFields.DATE_FROM]) as any) : undefined,
    query?.[AdminsFiltersFields.DATE_TO] ? (dayjs(query[AdminsFiltersFields.DATE_TO]) as any) : undefined,
  ];

  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        const dto = new GetAdminsListDTO(searchParams);
        dispatch(getAdminsList(dto));
      }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...query, [name]: value || undefined, page: 1 };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleRolesFilter = (value?: Roles) => {
    const newQuery = {
      ...query,
      page: 1,
      [AdminsFiltersFields.ROLE]: value,
    };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleDateRangeChange = (_: unknown, dateStrings: [string, string]) => {
    const newQuery = {
      ...query,
      page: 1,
      [AdminsFiltersFields.DATE_FROM]:
        formatDateForApiRequest(dateStrings[0], DateFormats.DATE_WITHOUT_HOURS) || undefined,
      [AdminsFiltersFields.DATE_TO]:
        formatDateForApiRequest(dateStrings[1], DateFormats.DATE_WITHOUT_HOURS) || undefined,
    };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <Input
            placeholder={t('users_list_name')}
            name={AdminsFiltersFields.FULL_NAME}
            onChange={handleSearch}
            value={query?.[AdminsFiltersFields.FULL_NAME] || ''}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Select
            options={getRoleOptions(t)}
            placeholder={t('users_list_role')}
            onChange={handleRolesFilter}
            value={query?.[AdminsFiltersFields.ROLE] || undefined}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('users_list_region')}
            name={AdminsFiltersFields.REGION}
            onChange={handleSearch}
            value={query?.[AdminsFiltersFields.REGION] || ''}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('users_list_email')}
            name={AdminsFiltersFields.EMAIL}
            onChange={handleSearch}
            value={query?.[AdminsFiltersFields.EMAIL] || ''}
            size="large"
            allowClear
          />
        </th>
        <th>
          <RangePicker
            placeholder={[t('users_list_range_from'), t('users_list_range_to')]}
            onChange={handleDateRangeChange}
            size="middle"
            format={DateFormats.DATE_WITHOUT_HOURS}
            value={dateRangeValue}
          />
        </th>
        {canManageAdmins && <th />}
      </StyledFilterRow>
    </thead>
  );
};

export default memo(AdminsFiltersContainer);
